import { useMemo } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Label,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { EVENTS_METRICS_BAR_FILL } from "../constants/dashboard-events-metrics.constants";
import type { EventsMetricsChartRow } from "../types/events-metrics";
import { buildHorizontalBarXAxisTicks } from "../utils/events-metrics-chart.utils";

function ChartTooltip({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ payload: EventsMetricsChartRow }>;
}) {
	if (!(active && payload?.length)) {
		return null;
	}
	const row = payload[0]?.payload;
	if (!row) {
		return null;
	}
	return (
		<div className="rounded-lg border border-[#E9EAEB] bg-white px-3 py-2 shadow-lg">
			<p className="font-medium text-primary text-sm">{row.name}</p>
			<p className="text-sm text-tertiary">{row.count}</p>
		</div>
	);
}

export type DashboardEventsBarChartProps = {
	rows: EventsMetricsChartRow[];
};

export function DashboardEventsBarChart({
	rows,
}: DashboardEventsBarChartProps) {
	const maxCount = Math.max(0, ...rows.map((r) => r.count));
	const { domain, ticks } = useMemo(
		() => buildHorizontalBarXAxisTicks(maxCount),
		[maxCount]
	);

	const height = Math.min(640, Math.max(280, rows.length * 30 + 100));

	return (
		<div className="flex min-h-0 w-full min-w-0 gap-0.5 sm:gap-1">
			<div
				aria-hidden
				className="flex w-6 shrink-0 items-center justify-center sm:w-8"
			>
				<span className="origin-center whitespace-nowrap font-medium text-tertiary text-xs [transform:rotate(-90deg)] sm:text-sm">
					Eventos
				</span>
			</div>
			<div
				className="min-h-0 min-w-0 flex-1 sm:min-w-[372px]"
				style={{ height }}
			>
				<ResponsiveContainer
					height={height}
					minHeight={0}
					minWidth={0}
					width="100%"
				>
					<BarChart
						barCategoryGap="12%"
						data={rows}
						layout="vertical"
						margin={{ left: 0, right: 4, top: 8, bottom: 40 }}
					>
						<CartesianGrid
							horizontal={false}
							stroke="#E5E7EB"
							strokeDasharray="3 3"
						/>
						<XAxis
							axisLine={{ stroke: "#E5E7EB" }}
							domain={domain}
							tick={{ fill: "#535862", fontSize: 11 }}
							tickLine={{ stroke: "#E5E7EB" }}
							ticks={ticks}
							type="number"
						>
							<Label
								offset={24}
								position="bottom"
								style={{ fill: "#535862", fontSize: 12 }}
								value="Quantidade de Eventos"
							/>
						</XAxis>
						<YAxis
							axisLine={false}
							dataKey="name"
							tick={{ fill: "#414651", fontSize: 11 }}
							tickLine={false}
							type="category"
							width={128}
						/>
						<Tooltip content={<ChartTooltip />} cursor={{ fill: "#F5F5F5" }} />
						<Bar
							dataKey="count"
							fill={EVENTS_METRICS_BAR_FILL}
							maxBarSize={16}
							name="Eventos"
							radius={[0, 4, 4, 0]}
						/>
					</BarChart>
				</ResponsiveContainer>
			</div>
		</div>
	);
}
